import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

import { EvaChart } from "constants/charts.enums";

import { useGlobalProductionSettings } from "hooks";
import { useChartEntities } from "hooks/charts";

import { EntityKind } from "models/entityKind";

import { useChartsContext } from "../../../contexts";
import { GlobalProductionSettings } from "../../../hooks/useGlobalProductionSettings";
import { INormalizeBySetting } from "../../../models";
import { updateChartByPath } from "../../../store/features";

export interface GlobalSettings {
  usingGlobalFocus: boolean;
  usingGlobalTimestep: boolean;
  usingGlobalProduction: boolean;
  usingGlobalNormalizeBy: boolean;
  usingAllGlobalSettings: boolean;
}

const useChartVsGlobalSettings = (): GlobalSettings => {
  const dispatch = useDispatch();
  const { id, entityKind, initialChartSettings } = useChartsContext();
  const globalSettings = useGlobalProductionSettings(entityKind);
  const {
    chartType: chartTypeState,
    normalizeBy: normalizeByState,
    chartFocus: chartFocusState,
    multiProductFocus: multiProductFocusState,
    timeStep: timeStepState,
    source: sourceState,
    chartLegend: legendState
  } = useChartEntities(id);
  const normalizeBySettings = normalizeByState?.properties;
  const isChartFocusActive = chartFocusState?.active;
  const isLegendActive = legendState?.active;
  const isFirstRender = useRef(true);
  const isMidstreamChart = entityKind === EntityKind.Facility;

  useEffect(() => {
    // Only apply the saved settings logic on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!initialChartSettings?.features?.legend?.active) {
        // If widget was saved with legend off, respect that setting on first load
        return;
      }
    }
    openLegendConditionally(!isLegendActive && isChartFocusActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChartFocusActive]);

  const isUsingGlobalNormalizeBy = (
    settings: INormalizeBySetting,
    globalSettings: GlobalProductionSettings
  ): boolean => {
    // If both are disabled, they're equivalent
    // Midstream charts have no global normalization
    if (!settings?.useNormalizeBy && !globalSettings?.normalizeBy) {
      return true;
    }

    // If both are enabled, compare only the relevant properties
    if (settings?.useNormalizeBy && !!globalSettings?.normalizeBy) {
      const localNormalizeSettings = {
        field: settings.field,
        per: settings.per,
        unit: settings.unit,
        useMultilinearNormalization: settings.useMultilinearNormalization,
        threshold: settings.threshold,
        lowerScalar: settings.lowerScalar,
        higherScalar: settings.higherScalar
      };

      const globalNormalizeSettings = {
        field: globalSettings.normalizeBy.field,
        per: globalSettings.normalizeBy.per,
        unit: globalSettings.normalizeBy.unit,
        useMultilinearNormalization:
          globalSettings.normalizeBy.useMultilinearNormalization,
        threshold: globalSettings.normalizeBy.threshold,
        lowerScalar: globalSettings.normalizeBy.lowerScalar,
        higherScalar: globalSettings.normalizeBy.higherScalar
      };

      return (
        JSON.stringify(localNormalizeSettings) === JSON.stringify(globalNormalizeSettings)
      );
    }
    return false;
  };

  const openLegendConditionally = (condition: boolean) => {
    if (condition) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.legend.active`,
          value: !isLegendActive
        })
      );
    }
  };

  return useMemo(() => {
    const usingGlobalFocus: boolean = !chartFocusState?.active;
    const usingGlobalTimestep: boolean =
      globalSettings?.timeStep === timeStepState?.properties.value || isMidstreamChart;
    // A chart is using global production when its source mode matches global AND there is
    // no per-chart specific-public-source override (specificPublicSource == null means
    // "org priority" — the default). gDC == 0, so guard with != null, never truthiness.
    const specificPublicSource = sourceState?.properties?.specificPublicSource;
    const usingGlobalProduction: boolean =
      (globalSettings?.source === sourceState?.properties.value &&
        specificPublicSource == null) ||
      isMidstreamChart;
    const usingGlobalNormalizeBy: boolean =
      isUsingGlobalNormalizeBy(normalizeBySettings, globalSettings) || isMidstreamChart;

    const usingMultiProduct: boolean =
      multiProductFocusState?.properties?.products?.length > 0 &&
      (chartTypeState === EvaChart.TotalRateDate ||
        chartTypeState === EvaChart.TotalRateDateMidstream);

    return {
      usingGlobalFocus,
      usingGlobalTimestep,
      usingGlobalProduction,
      usingGlobalNormalizeBy,
      usingAllGlobalSettings:
        usingGlobalFocus &&
        usingGlobalTimestep &&
        usingGlobalProduction &&
        usingGlobalNormalizeBy &&
        !usingMultiProduct
    };
  }, [
    JSON.stringify(globalSettings?.normalizeBy),
    globalSettings?.source,
    globalSettings?.timeStep,

    JSON.stringify(normalizeBySettings),
    normalizeBySettings?.useNormalizeBy,
    chartFocusState?.active,
    timeStepState.properties.value,
    sourceState.properties.value,
    sourceState.properties.specificPublicSource,
    multiProductFocusState
  ]);
};
export default useChartVsGlobalSettings;
